import { useEffect, useState } from 'react';

import {
  usePatchTelegramSettings,
  useSendTelegramTestMessage,
  useTelegramMode,
  useTelegramSettings,
  useTestTelegramConnection,
} from '../../hooks/use-settings';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';
import { TelegramChatsPanel } from './TelegramChatsPanel';

export function TelegramSection() {
  const { data, isLoading } = useTelegramSettings();
  const mode = useTelegramMode();
  const patch = usePatchTelegramSettings();
  const test = useTestTelegramConnection();
  const testMessage = useSendTelegramTestMessage();
  const toast = useToast();

  const [leadsChatId, setLeadsChatId] = useState('');
  const [alertsChatId, setAlertsChatId] = useState('');
  const [token, setToken] = useState('');
  const [testTarget, setTestTarget] = useState<'leads' | 'alerts' | 'team'>('leads');

  useEffect(() => {
    if (!data) return;
    setLeadsChatId(data.settings.leads_chat_id?.toString() ?? '');
    setAlertsChatId(data.settings.alerts_chat_id?.toString() ?? '');
  }, [data]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    patch.mutate(
      {
        leads_chat_id: leadsChatId ? Number(leadsChatId) : null,
        alerts_chat_id: alertsChatId ? Number(alertsChatId) : null,
        ...(token.trim() ? { bot_token: token.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.push('Настройки Telegram сохранены', 'success');
          setToken('');
        },
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось сохранить настройки'),
      },
    );
  }

  if (isLoading || !data) return <Skeleton className="h-24 w-full" />;
  const settings = data.settings;

  return (
    <div className="space-y-4">
      {mode.data && !mode.data.real && (
        <div className="max-w-lg rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" role="status">
          Тестовый режим (TG_MODE=mock): сообщения никуда не уходят, только сохраняются в ленте на dev-странице.
        </div>
      )}

      <form onSubmit={submit} className="max-w-md space-y-3 rounded border border-border p-3">
        <div>
          <label className="mb-1 block text-xs text-muted-fg" htmlFor="tg-token">
            Токен бота {settings.bot_token_set && <span className="font-mono text-fg">({settings.bot_token_masked})</span>}
          </label>
          <Input
            id="tg-token"
            type="password"
            autoComplete="off"
            placeholder={settings.bot_token_set ? 'Оставьте пустым, чтобы не менять' : 'Токен из @BotFather'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-fg" htmlFor="tg-leads">
            Chat ID для лидов
          </label>
          <Input id="tg-leads" value={leadsChatId} onChange={(e) => setLeadsChatId(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-fg" htmlFor="tg-alerts">
            Chat ID для алертов
          </label>
          <Input id="tg-alerts" value={alertsChatId} onChange={(e) => setAlertsChatId(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={patch.isPending}>
            Сохранить
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!settings.bot_token_set || test.isPending}
            onClick={() =>
              test.mutate(undefined, {
                onSuccess: (res) => toast.push(`Связь есть: ${res.bot_name ?? res.bot_username ?? 'бот'}`, 'success'),
                onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось проверить связь'),
              })
            }
          >
            Проверить связь
          </Button>
          {settings.verified_at && <Badge tone="success">Подключено{settings.bot_username ? `: @${settings.bot_username}` : ''}</Badge>}
        </div>
      </form>

      <div className="max-w-md space-y-2 rounded border border-border p-3">
        <h3 className="text-sm font-semibold">Тестовое сообщение</h3>
        <div className="flex items-center gap-2">
          <select
            className="h-9 rounded border border-border bg-card px-2 text-sm"
            value={testTarget}
            onChange={(e) => setTestTarget(e.target.value as typeof testTarget)}
            aria-label="Куда отправить"
          >
            <option value="leads">Заявки</option>
            <option value="alerts">Алерты</option>
            <option value="team">Команда</option>
          </select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={testMessage.isPending}
            onClick={() =>
              testMessage.mutate(
                { chat_role: testTarget },
                {
                  onSuccess: () => toast.push('Тестовое сообщение отправлено', 'success'),
                  onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось отправить'),
                },
              )
            }
          >
            Отправить тестовое сообщение
          </Button>
        </div>
      </div>

      <TelegramChatsPanel />
    </div>
  );
}
